import { unstable_batchedUpdates } from "react-dom";
import { setNumberValueAs } from "../../../../../../../utils/react-hook-form";
import { Select } from "../../../../../../Inputs";
import { Text } from "../../../../../../Inputs/react-hook-form";
import {
  getSchemaPropertyFormPath,
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../form/form";
import { FieldWrapper } from "../__styles__/SidebarColumn";
import { WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import type { InputSettingsFormProps } from "./FieldSettingsView";
import { DefaultValuesSection } from "./shared/DefaultValuesSection";
import { DisplaySettings } from "./shared/DisplaySettings";
import { FieldPermissionsConfig } from "./shared/FieldPermissionsConfig";
import { FieldSettingsConfig } from "./shared/FieldSettingsConfig";
import { HelperTextInput } from "./shared/HelperTextInput";
import { NumberDynamicDefaultInput } from "./shared/NumberDynamicDefaultInput";
import { MinMaxInputs } from "./shared/NumberMinMaxInputs";
import { NumberStaticDefaultInput } from "./shared/NumberStaticDefaultInput";
import { PlaceholderTextInput } from "./shared/PlaceholderTextInput";

const prefixPostfixOptions = [
  { label: "%", value: "%" },
  { label: "$", value: "$" },
];

export function NumberFieldSettings({
  fieldPath,
  isReadOnly,
  isModuleInput,
}: InputSettingsFormProps) {
  const isFormulaField = !!useBuilderWatch(
    getSchemaPropertyFormPath(fieldPath, "formula"),
  );

  return (
    <WidgetSettingsContainer>
      <FieldSettingsConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isReadOnly={isReadOnly}
        isFormulaField={isFormulaField}
      />
      <HelperTextInput fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <PlaceholderTextInput fieldPath={fieldPath} isReadOnly={isReadOnly} />
      {!isFormulaField && (
        <StepValueInput
          fieldPath={fieldPath}
          isReadOnly={isReadOnly}
          isModuleInput={isModuleInput}
        />
      )}
      <PrefixPostfixSelect
        fieldPath={fieldPath}
        isReadOnly={isReadOnly}
        isModuleInput={isModuleInput}
      />
      {!isFormulaField && (
        <MinMaxInputs fieldPath={fieldPath} isReadOnly={isReadOnly} />
      )}
      <DefaultValuesInputs fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <FieldPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <DisplaySettings fieldPath={fieldPath} isReadOnly={isReadOnly} />
    </WidgetSettingsContainer>
  );
}

function StepValueInput({
  fieldPath,
  isReadOnly,
}: Omit<InputSettingsFormProps, "inputType">) {
  const { register, getFieldState } = useBuilderFormContext();
  const stepPropertyFormPath = getUiSchemaPropertyFormPath(fieldPath, "step");
  const stepInputError = getFieldState(stepPropertyFormPath)?.error;

  return (
    <Text
      label="Step value"
      type="number"
      error={stepInputError?.message}
      disabled={isReadOnly}
      {...register(stepPropertyFormPath, { setValueAs: setNumberValueAs })}
    />
  );
}

function PrefixPostfixSelect({
  fieldPath,
  isReadOnly,
  isModuleInput,
}: Omit<InputSettingsFormProps, "inputType">) {
  const { setValue } = useBuilderFormContext();

  const prefixPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "prefix",
  );
  const postfixPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "postfix",
  );

  const prefixValue = useBuilderWatch(prefixPropertyFormPath);
  const postfixValue = useBuilderWatch(postfixPropertyFormPath);
  const prefixPostfixValue = prefixValue || postfixValue;

  function batchUpdatePrefixPostfix(
    params:
      | {
          prefix: string;
          postfix?: never;
        }
      | {
          prefix?: never;
          postfix: string;
        }
      | undefined,
  ) {
    const { prefix, postfix } = params ?? {};

    unstable_batchedUpdates(() => {
      setValue(prefixPropertyFormPath, prefix, { shouldValidate: true });
      setValue(postfixPropertyFormPath, postfix, { shouldValidate: true });
    });
  }

  return (
    <Select
      // not a real value being set in the form, just used as a name
      name={getUiSchemaPropertyFormPath(fieldPath, "prefixPostfix")}
      options={prefixPostfixOptions}
      value={prefixPostfixValue}
      onChange={(value: Maybe<string>) => {
        switch (value) {
          case "%":
            batchUpdatePrefixPostfix({ postfix: value });
            return;
          case "$":
            batchUpdatePrefixPostfix({ prefix: value });
            return;
          default:
            batchUpdatePrefixPostfix(undefined);
        }
      }}
      label="Format"
      disabled={isModuleInput || isReadOnly}
      isClearable={true}
    />
  );
}

function DefaultValuesInputs({
  fieldPath,
  isReadOnly,
}: Omit<InputSettingsFormProps, "inputType" | "isModuleInput">) {
  return (
    <DefaultValuesSection showTooltip>
      <FieldWrapper data-testid="dynamic-default-value-wrapper">
        <NumberDynamicDefaultInput
          fieldPath={fieldPath}
          isReadOnly={isReadOnly}
        />
      </FieldWrapper>
      <FieldWrapper data-testid="static-default-value-wrapper">
        <NumberStaticDefaultInput
          fieldPath={fieldPath}
          isReadOnly={isReadOnly}
        />
      </FieldWrapper>
    </DefaultValuesSection>
  );
}
